import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { ErrorHandlerService } from '../core/services/error-handler.service';
import { JwtService } from 'app/core/services/jwt.service';
import { lastValueFrom, Observable } from 'rxjs';

/**
 * Handles HTTP requests related to authentication, account activation,
 * password recovery and OTP validation against the security service.
 */
@Injectable()
export class AuthService {
  private apiUrl: string = environment.API_URL;
  private securityUrl: string = `${environment.API_URL}/auth`;

  constructor(
    private http: HttpClient,
    private jwtService: JwtService,
    private errorHandler: ErrorHandlerService
  ) {}

  /**
   * Authenticates a service with the given credentials and persists the received token.
   * @param credentials User access credentials (username, password, sid, etc.)
   * @returns Observable with the login response
   */
  public createServiceLogin(credentials: any): Observable<any> {
    const url = `${this.securityUrl}/auth/service/login/`;

    return this.http.post<any>(url, credentials).pipe(
      map((resp) => {
        if (resp.token) {
          this.jwtService.saveToken(resp.token);
        }
        return resp;
      }),
      catchError(this.errorHandler.handleHttpError)
    );
  }

  /**
   * Checks whether an account exists for the given credentials without creating a session.
   * @param credentials Credentials to validate
   * @returns Observable with the result of the check
   */
  public testServiceLogin(credentials: any): Observable<any> {
    const url = `${this.securityUrl}/auth/service/test`;
    return this.http
      .post<any>(url, credentials)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Requests an account activation token for the given credentials.
   * @param credentials Data of the user to activate
   * @returns Observable with the generated activation token
   */
  public createActivationToken(credentials: any): Observable<any> {
    const url = `${this.securityUrl}/auth/tokens/activation`;
    return this.http
      .post<any>(url, credentials)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Validates an account activation or password reset token.
   * @param token Token received by email
   * @param operation Operation type (`activation` | `reset`)
   * @returns Observable with the data associated with the token
   */
  public validateAccountPasswordToken(token: string, operation: string): Observable<any> {
    const url = `${this.securityUrl}/auth/tokens/${operation}/${token}`;
    return this.http.get<any>(url).pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Sets the account password for an activation or reset operation.
   * @param credentials Form data, including the new password
   * @param operation Operation type (`activation` | `reset`)
   * @returns Observable with the server response
   */
  public setAccountPassword(credentials: any, operation: string): Observable<any> {
    const url = `${this.securityUrl}/auth/account/${operation}`;
    return this.http
      .post<any>(url, credentials)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Requests a password recovery email to be sent.
   * @param credentials Data of the user requesting the reset
   * @returns Observable with the server response
   */
  public requestAccountReset(credentials: any): Observable<any> {
    const url = `${this.securityUrl}/auth/reset-password`;
    return this.http
      .post<any>(url, credentials)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Updates a user's data in the SSO service.
   * @param userId User identifier
   * @param userData Data to update
   * @returns Observable with the updated user
   */
  public updateSsoUser(userId: string, userData: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'x-auth-token': this.jwtService.getToken() })
    };

    const url = `${this.apiUrl}/core/users/${userId}`;
    return this.http
      .put<any>(url, userData, httpOptions)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Updates a user's data in the security (auth) service.
   * @param userId User identifier
   * @param userData Data to update
   * @returns Observable with the updated user
   */
  public updateSecurityUser(userId, userData): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({ 'x-auth-token': this.jwtService.getToken() })
    };

    const url = `${this.apiUrl}/core/users/${userId}`;
    return this.http
      .put<any>(url, userData, httpOptions)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }

  /**
   * Removes the locally stored session token.
   */
  public logout(): void {
    this.jwtService.destroyToken();
  }

  /**
   * Verifies a token received by email (activation, reset, etc.) and returns its payload.
   * @param token Token to verify
   * @returns Promise with the token payload
   */
  public verifyToken(token: string): Promise<any> {
    const url = `${this.securityUrl}/auth/verifyToken/${token}`;
    return lastValueFrom(this.http.get<any>(url));
  }

  /**
   * Verifies a token and generates a reduced-scope token (scoped token) from it.
   * @param token Token to verify
   * @returns Promise with the generated scoped token
   */
  public verifyTokenAndGenerateScopedToken(token: string): Promise<any> {
    const url = `${this.securityUrl}/auth/verifyToken/${token}/scope`;
    return lastValueFrom(this.http.post<any>(url, {}));
  }

  /**
   * Verifies the scoped token issued after a login with two-factor authentication (2FA)
   * pending OTP verification.
   * @param token Scoped token associated with the pending login
   * @returns Promise with the verification result
   */
  public verifyTwoFactorToken(token: string): Promise<any> {
    const url = `${this.securityUrl}/auth/tokens/otp-jwt/validate`;
    const httpOptions = {
      headers: new HttpHeaders({ 'x-access-token': token })
    };

    return lastValueFrom(
      this.http
        .post<any>(url, null, httpOptions)
        .pipe(catchError(this.errorHandler.handleHttpError))
    );
  }

  /**
   * Validates the OTP code entered by the user and, if correct, persists the session token.
   * @param token Scoped token associated with the pending login
   * @param otpCode OTP code entered by the user
   * @returns Observable with the login response
   */
  public validateOtpCode(token: string, otpCode: string): Observable<any> {
    const url = `${this.securityUrl}/auth/service/otp/validate`;
    const httpOptions = {
      headers: new HttpHeaders({ 'x-access-token': token })
    };

    return this.http.post<any>(url, { otpCode }, httpOptions).pipe(
      map((resp) => {
        this.jwtService.saveToken(resp.token);
        return resp;
      }),
      catchError(this.errorHandler.handleHttpError)
    );
  }

  /**
   * Requests a new OTP code to be resent for the login pending verification.
   * @param token Scoped token associated with the pending login
   * @returns Observable with the server response
   */
  public resendOtpCode(token: string): Observable<any> {
    const url = `${this.securityUrl}/auth/service/otp/resend`;
    const httpOptions = {
      headers: new HttpHeaders({ 'x-access-token': token })
    };

    return this.http
      .post<any>(url, null, httpOptions)
      .pipe(catchError(this.errorHandler.handleHttpError));
  }
}
